/** @jsxImportSource @emotion/react */
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ExpertsFilters from "./ExpertsFilters";
import ExpertsTable from "./ExpertsTable";
import ExpertCard from "./ExpertCard";
import { Container, Backdrop, Fade, Box, Button, Card, CardContent, Grid, Fab, Dialog, Pagination} from '@mui/material';
import { ExpertDataArrayType } from "../../types/dataTypes";
import { pulseAnimation } from '../../styles/styles';
import { useTheme } from '@mui/material/styles';
import { ArrowForward, KeyboardArrowUp, SwitchRight } from "@mui/icons-material";
import ScrollTop from "../Navigation/ScrollTop";
import AddExpert from "./AddExpert";
import UpdateExpert from "./UpdateExpert";
import MetadataManagement from "../Shared/MetadataManagement";
import CustomMenu from "../Shared/CustomMenue";
import { getMenuItems, initialFiltersExperts, handleCvTemplateDialoge, handleCvFormaterDialoge, PAGE_LIMIT } from "../../utils/consts";
import { useToggle } from "../../hooks/useToggle";

type ReferencesViewProps = {
  userRoles: string[];
};

function ExpertsView({ userRoles }: ReferencesViewProps) {
  const navigate = useNavigate();
  const { expertID } = useParams<{ expertID: string }>();
  
  const [data, setData] = useState<ExpertDataArrayType>([]);
  const [filteredData, setFilteredData] = useState<ExpertDataArrayType>([]);
  const [page, setPage] = useState(1);
  const [pageTotal, setPageTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'cvYear', direction: "desc" });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // Card view is the default - the dense DataGrid hides too much (Experience, Regions, etc.) at a
  // glance, so "Table View" is offered as the alternate, denser option (mirrors ReferencesView).
  const [isTableView, setIsTableView] = useState<boolean>(false);

  const [isDrawerAdvancedFilterOpen, toggleDrawerAdvancedFilter] = useToggle(false);
  const [openAddDialog, toggleAddDialog] = useToggle(false);
  const [manageMetaDataDialogOpen, toggleMetaDataDialog] = useToggle(false);

  const theme = useTheme();
  const accessToken = localStorage.getItem('accessToken') || '';

  // Derived state from URL parameter
  const selectedExpert = expertID ? parseInt(expertID, 10) : 0;
  const isDrawerDetailsOpen = !!expertID && !isNaN(selectedExpert);

  // Initialize state for filters and available options
  const [filters, setFiltersState] = useState(initialFiltersExperts);
  // Bumped after a save to re-fetch the current page without touching filters/page/sort.
  const [refreshKey, setRefreshKey] = useState(0);
  const refreshList = () => setRefreshKey((k) => k + 1);
  // Any filter change goes back to page 1 (paging/sorting alone keeps the page).
  const setFilters = (next: any) => {
    setFiltersState(next);
    setPage(1);
  };

  // Handle URL parameter changes
  useEffect(() => {
    if (expertID) {
      const id = parseInt(expertID, 10);
      if (isNaN(id)) {
        // Invalid expert ID, redirect to experts list
        navigate('/experts', { replace: true });
      }
    }
  }, [expertID, navigate]);

  const handleAnchorState = (event?: React.MouseEvent<HTMLElement>, action: 'open' | 'close' = 'open') => {
    if (action === 'close') {
      setAnchorEl(null);
    }
    else if (action === 'open' && event) {
      setAnchorEl(event.currentTarget);
    }
  };  

  const handledetailsUpdateDialogOpen = (expertID: number) => {
    console.log(`Opening details for expert ID: ${expertID}`);
    navigate(`/experts/${expertID}`);
  };

  const handleDetailsClose = () => {
    navigate('/experts');
  };

const menuItems = getMenuItems(
  (action: "open" | "close") => toggleAddDialog(action === "open"),
  (action: "open" | "close") => toggleMetaDataDialog(action === "open"),
  handleCvTemplateDialoge,
  handleCvFormaterDialoge
);

  return (
    <Container>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          transition: 'margin 0.3s ease',
          marginLeft: isDrawerAdvancedFilterOpen ? `20vw` : 0,
          marginRight: isDrawerDetailsOpen ? `40vw` : 0,
          overflow: 'auto',
        }}
      >
        <Card sx={{ margin: 1 }}>
          <CardContent>
            <ExpertsFilters data={data} setData={setData} setFilteredData={setFilteredData} setPageTotal={setPageTotal}
              page={page} setLoading={setLoading} filters={filters} setFilters={setFilters} sortConfig={sortConfig}
              isDrawerOpen={isDrawerAdvancedFilterOpen} setIsDrawerOpen={toggleDrawerAdvancedFilter} refreshKey={refreshKey} />
            <Grid container spacing={2} justifyContent="space-between">
              <Grid item>
                <Button variant="text" color="primary" onClick={() => toggleDrawerAdvancedFilter()}
                  startIcon={<ArrowForward />}>
                  Advanced Filters
                </Button>
              </Grid>
              <Grid item>
                <Button size="small" variant="outlined" color="primary" onClick={() => setIsTableView(!isTableView)}
                  startIcon={<SwitchRight />}>
                  {isTableView ? 'Card View' : 'Table View'}
                </Button>
              </Grid>
              <Grid item>
              <CustomMenu
                    anchorEl={anchorEl}
                    handleClose={() => handleAnchorState(undefined, 'close')}
                    menuItems={menuItems}
                    handleManageOpen={handleAnchorState}
                    userRoles={userRoles}
                    />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <div id="back-to-top-anchor" />
            <ScrollTop>
              <Fab color="primary" size="small" aria-label="scroll back to top">
                <KeyboardArrowUp />
              </Fab>
            </ScrollTop>
          </Grid>
          <Grid container spacing={1} justifyContent='space-between'>
            {isTableView ? (
              <Grid container spacing={0.5} justifyContent='center'>
                <ExpertsTable
                  data={filteredData} page={page} setPage={setPage} pageTotal={pageTotal}
                  sortConfig={sortConfig} setSortConfig={setSortConfig}
                  handleOpenUpdateDialog={handledetailsUpdateDialogOpen}
                  loading={loading}
                />
              </Grid>
            ) : (
              <Grid container spacing={2} justifyContent='center'>
                {filteredData.map((item, index) => (
                  <Grid item xs={12} sm={12} md={6} key={item.expertID ?? index}>
                    <ExpertCard
                      item={item}
                      onDetailsClick={handledetailsUpdateDialogOpen}
                      accessToken={accessToken}
                    />
                  </Grid>
                ))}
                {pageTotal > PAGE_LIMIT && (
                  <Grid item xs={12}>
                    <Box display="flex" justifyContent="center" paddingTop={4} paddingBottom={4}>
                      <Pagination
                        count={Math.max(Math.ceil(pageTotal / PAGE_LIMIT), 1)}
                        page={page}
                        onChange={(_event, value) => setPage(value)}
                        color="primary"
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>
            )}
          </Grid>
          {selectedExpert !== 0 ? (
          <UpdateExpert
            setLoading={setLoading}
            accessToken={accessToken}
            expertID={selectedExpert}
            setFilters={setFilters}
            onSaved={refreshList}
            isDrawerOpen={isDrawerDetailsOpen}
            setIsDrawerDetailsOpen={handleDetailsClose}
          />
        ) : null}
          <Backdrop open={loading} style={{ zIndex: 9999, color: '#fff' }}>
            <Fade in={loading}>
              <img src={theme.logo} alt="Company Logo" css={pulseAnimation} />
            </Fade>
          </Backdrop>
        </Grid>
      </Box>
      <Dialog
        open={openAddDialog}
        onClose={() => toggleAddDialog(false)}
        aria-labelledby="add-expert-dialog"
        fullWidth={true}
        maxWidth="lg"
      >
        <AddExpert setLoading={setLoading} accessToken={accessToken} expertID={0} setDetailsDrawerOpen={toggleAddDialog} 
        setFilters={setFilters} onSaved={refreshList}/>
      </Dialog>
      <Dialog open={manageMetaDataDialogOpen} onClose={() => toggleMetaDataDialog(false)} fullWidth maxWidth="sm">
         <MetadataManagement handleClose={() => toggleMetaDataDialog(false)} database="experts" />
      </Dialog>
    </Container>
  );
};

export default ExpertsView;